import { Injectable, isDevMode } from '@angular/core';
import { Observable } from 'rxjs';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { environment } from '../../environments/environment';

const COLLECTION = 'NarrativeImpactReport';
const COVER_PHOTO_COLLECTION = 'ALCoverPhoto';
const PLACEHOLDER_PHOTO_COLLECTION = 'ALPlaceholderImage';
const EDUCATION_IMPACT_REPORT_COLLECTION = 'EducationImpactReport';
const COUNSELLING_IMPACT_REPORT_COLLECTION = 'CounselingImpactReport';
const CONTACT_PHOTO_COLLECTION = 'ALContactPhoto';

export interface CountDatum {
  label: string;
  count: number;
}

export interface CoverPhoto {
  id?: string;
  photoUrl: string;
  childCode?: string;
  childName?: string;
  createdAt: any;
  createdBy: string;
  updatedAt?: any;
}

// A small reusable pool of decorative images (not tied to any one centre/quarter) that the
// generated report can drop into a gallery page that would otherwise end with a large blank
// gap below its photos — see gallerySectionHtml()/the placeholder-fill script in
// functions/src/narrative-impact-report.ts.
export interface PlaceholderPhoto {
  id?: string;
  photoUrl: string;
  createdAt: any;
  createdBy: string;
}

// Previously uploaded contact-page photos, kept so they can be reused across
// reports instead of re-uploaded each time.
export interface LibraryPhoto {
  id?: string;
  photoUrl: string;
  createdAt: any;
  createdBy: string;
}

/** A department impact report (Education / Counselling) already saved through its own report
 *  screen, as stored in Firestore, with `from`/`to` converted to Dates. */
export interface DepartmentImpactReport {
  id: string;
  reportType?: string;
  submitType?: string;
  from: Date;
  to: Date;
  [field: string]: any;
}

/** Which major sections go into the generated PDF; false leaves that section out. */
export interface ReportSections {
  cover: boolean;
  beneficiary: boolean;
  education: boolean;
  counselling: boolean;
  caseStudies: boolean;
  contact: boolean;
}

export interface BeneficiaryStats {
  bedOccupancyPercent: number;
  admissionCount: number;
  caregiverAccommodationCount: number;
  educationSessionCount: number;
  counsellingSessionCount: number;
}

export interface ProgressSubjectScore {
  subject: string;
  baseline: number;
  discharge: number;
}

export interface CopingSegment {
  label: string;
  value: number;
}

export interface EducationSection {
  introText: string;
  ageRangeText: string;
  toddlerText: string;
  teacherSessions: number;
  volunteerSessions: number;
  specialDaysObserved: number;
  highlights: string[];
  progressChart: ProgressSubjectScore[];
  progressNote: string;
  sessionPhotos: string[];
  activityPhotos: string[];
  volunteerPhotos: string[];
  // Optional Fabric.js-generated collage; when present the backend renders
  // this single image for the gallery instead of the justified-row layout.
  sessionCollageUrl?: string;
  activityCollageUrl?: string;
  volunteerCollageUrl?: string;
  // True when the exported collage is sized to occupy a whole report page (see
  // COLLAGE_FULL_PAGE_HEIGHT_PX) — the backend gives it a dedicated page instead
  // of flowing it inline.
  sessionCollageFullPage?: boolean;
  activityCollageFullPage?: boolean;
  volunteerCollageFullPage?: boolean;
}

export interface CounsellingSection {
  openingQuote: string;
  introParagraphs: string[];
  individualSessions: number;
  psychologicalInterventions: number;
  groupSessions: number;
  problemsAddressedParagraph: string;
  highlights: string[];
  interventionsToolsParagraph: string;
  admissionsDischargeParagraphs: string[];
  admissionCoping: CopingSegment[];
  dischargeCoping: CopingSegment[];
  interpretationBullets: string[];
  closingParagraph: string;
  sessionPhotos: string[];
  groupPhotos: string[];
  // Optional Fabric.js-generated collage, same convention as EducationSection above.
  sessionCollageUrl?: string;
  groupCollageUrl?: string;
  sessionCollageFullPage?: boolean;
  groupCollageFullPage?: boolean;
}

export interface CaseStudyData {
  braveheartLabel: string;
  name: string;
  gender: string;
  age: number;
  dob: string;
  city: string;
  state: string;
  diagnosis: string;
  hospital: string;
  photo: string;
  paragraphs: string[];
}

// Kept in sync by hand with WatermarkStyle in functions/src/narrative-impact-report.ts —
// the Angular app and the Cloud Functions codebase compile separately, so there's no
// shared import between them.
export type WatermarkStyle = 'wave' | 'circle' | 'none';

export interface NarrativeImpactReport {
  id?: string;
  centreCode: string;
  centreName: string;
  quarterLabel: string;
  periodLabel: string;
  from: Date;
  to: Date;
  submitType: 'draft' | 'final';
  createdAt: any;
  updatedAt?: any;
  createdBy: string;
  coverTitle?: string;
  coverPhotoUrl?: string;
  contactPhotoUrl?: string;
  watermarkStyle?: WatermarkStyle;
  placeholderPhotos?: string[];
  beneficiaryStats: BeneficiaryStats;
  ageWiseData: CountDatum[];
  genderWiseData: CountDatum[];
  hospitalWiseData: CountDatum[];
  beneficiaryRemarks?: string;
  sections?: ReportSections;
  education: EducationSection;
  counselling: CounsellingSection;
  caseStudies: CaseStudyData[];
}

// Indian financial-year quarters (April–March), relative to the FY's start year. Q4 runs Jan–Mar of
// the *following* calendar year, hence yearOffset.
const FY_QUARTERS = [
  { label: 'Q1', startMonth: 3, endMonth: 5, yearOffset: 0 },  // Apr – Jun
  { label: 'Q2', startMonth: 6, endMonth: 8, yearOffset: 0 },  // Jul – Sep
  { label: 'Q3', startMonth: 9, endMonth: 11, yearOffset: 0 }, // Oct – Dec
  { label: 'Q4', startMonth: 0, endMonth: 2, yearOffset: 1 },  // Jan – Mar (next calendar year)
];

// ── Reading saved department impact reports (EducationImpactReport / CounselingImpactReport) ──
// Shared by the narrative and annual report wizards.

/** A photo entry's URL: saved either as a plain URL or as a gallery-picker object. */
export function photoUrlOf(x: any): string {
  return typeof x === 'string' ? x : (x?.url || x?.fileUrl || x?.photoUrl || '');
}

/** Trimmed, non-empty, de-duplicated strings in their original order. */
export function uniqueStrings(list: string[]): string[] {
  return Array.from(new Set(list.map(s => (s || '').trim()).filter(Boolean)));
}

/** Report text is stored either as CKEditor HTML (<ul><li>… or <p>…) or as plain text; either way
 *  each list item / paragraph / line becomes one entry. */
export function htmlToLines(text: any): string[] {
  const raw = typeof text === 'string' ? text.trim() : '';
  if (!raw) return [];
  if (!/<[a-z][\s\S]*>/i.test(raw)) return raw.split(/\n+/).map(s => s.trim()).filter(Boolean);
  const doc = new DOMParser().parseFromString(raw, 'text/html');
  const items = Array.from(doc.querySelectorAll('li'));
  const nodes = items.length ? items : Array.from(doc.querySelectorAll('p'));
  const out = (nodes.length ? nodes.map(n => n.textContent || '') : [doc.body.textContent || ''])
    .map(s => s.replace(/\s+/g, ' ').trim());
  return out.filter(Boolean);
}

/** An education report's photos for one category ('education' | 'celebration' | 'volunteering'):
 *  uploaded files (`image`) plus gallery picks (`galleryImages`). Older reports saved
 *  galleryImages as one flat array, which was the education gallery. */
export function educationReportPhotos(reports: DepartmentImpactReport[], key: string): string[] {
  return uniqueStrings(reports.flatMap(r => {
    const gi = r['galleryImages'];
    const gallery = Array.isArray(gi) ? (key === 'education' ? gi : []) : (gi?.[key] || []);
    return [...(r['image']?.[key] || []), ...gallery].map(photoUrlOf);
  }));
}

/** A counselling report's gallery photos. */
export function counsellingReportPhotos(reports: DepartmentImpactReport[]): string[] {
  return uniqueStrings(reports.flatMap(r => (r['galleryImages'] || []).map(photoUrlOf)));
}

@Injectable({ providedIn: 'root' })
export class NarrativeImpactReportService {

  private readonly env = isDevMode() ? environment.testMode : environment.productionMode;

  constructor(private afs: AngularFirestore) {}

  async saveReport(data: Partial<NarrativeImpactReport>): Promise<string> {
    const ref = await this.afs.collection(COLLECTION).add({
      ...data,
      createdAt: new Date(),
    });
    return ref.id;
  }

  async updateReport(id: string, data: Partial<NarrativeImpactReport>): Promise<void> {
    await this.afs.collection(COLLECTION).doc(id).set(
      { ...data, updatedAt: new Date() },
      { merge: true }
    );
  }

  /** One saved report by id, or null if it doesn't exist. */
  async getReport(id: string): Promise<NarrativeImpactReport | null> {
    const snap = await this.afs.collection(COLLECTION).doc(id).get().toPromise();
    return snap?.exists ? ({ ...(snap.data() as any), id } as NarrativeImpactReport) : null;
  }

  getReports(centreCode?: string): Observable<NarrativeImpactReport[]> {
    return this.afs.collection<NarrativeImpactReport>(COLLECTION, ref => {
      const q = centreCode
        ? ref.where('centreCode', '==', centreCode).orderBy('createdAt', 'desc')
        : ref.orderBy('createdAt', 'desc');
      return q;
    }).valueChanges({ idField: 'id' });
  }

  async addCoverPhoto(photoUrl: string, createdBy: string, childCode?: string, childName?: string): Promise<string> {
    const now = new Date();
    const ref = await this.afs.collection(COVER_PHOTO_COLLECTION).add({
      photoUrl,
      createdBy,
      ...(childCode ? { childCode } : {}),
      ...(childName ? { childName } : {}),
      createdAt: now,
      updatedAt: now,
    });
    return ref.id;
  }

  getCoverPhotos(): Observable<CoverPhoto[]> {
    return this.afs.collection<CoverPhoto>(COVER_PHOTO_COLLECTION, ref =>
      ref.orderBy('createdAt', 'desc')
    ).valueChanges({ idField: 'id' });
  }

  async deleteCoverPhoto(id: string): Promise<void> {
    await this.afs.collection(COVER_PHOTO_COLLECTION).doc(id).delete();
  }

  async addContactPhoto(photoUrl: string, createdBy: string): Promise<string> {
    const ref = await this.afs.collection(CONTACT_PHOTO_COLLECTION).add({ photoUrl, createdBy, createdAt: new Date() });
    return ref.id;
  }

  getContactPhotos(): Observable<LibraryPhoto[]> {
    return this.afs.collection<LibraryPhoto>(CONTACT_PHOTO_COLLECTION, ref =>
      ref.orderBy('createdAt', 'desc')
    ).valueChanges({ idField: 'id' });
  }

  async deleteContactPhoto(id: string): Promise<void> {
    await this.afs.collection(CONTACT_PHOTO_COLLECTION).doc(id).delete();
  }

  /** Education impact reports saved for the centre that fall inside the quarter. */
  getEducationImpactReports(centreCode: string, start: Date, end: Date): Promise<DepartmentImpactReport[]> {
    return this.getImpactReportsInRange(EDUCATION_IMPACT_REPORT_COLLECTION, centreCode, start, end);
  }

  /** Counselling impact reports saved for the centre that fall inside the quarter. */
  getCounsellingImpactReports(centreCode: string, start: Date, end: Date): Promise<DepartmentImpactReport[]> {
    return this.getImpactReportsInRange(COUNSELLING_IMPACT_REPORT_COLLECTION, centreCode, start, end);
  }

  // Filtered by date in memory rather than in the query, so no composite (centreCode + from)
  // index is needed. A report counts when its whole range sits inside the quarter (a day of
  // slack either side, since saved ranges are normalised to day boundaries): that takes the
  // quarter's monthly and quarterly reports, and leaves out half-year / yearly ones that merely
  // overlap it.
  private async getImpactReportsInRange(collection: string, centreCode: string, start: Date, end: Date): Promise<DepartmentImpactReport[]> {
    const DAY_MS = 24 * 60 * 60 * 1000;
    const snap = await this.afs.collection(collection, ref => ref.where('centreCode', '==', centreCode)).get().toPromise();
    const toDate = (v: any): Date | null => v?.toDate ? v.toDate() : v instanceof Date ? v : null;
    return (snap?.docs || [])
      .map(d => {
        const data: any = d.data();
        return { ...data, id: d.id, from: toDate(data.from), to: toDate(data.to) } as DepartmentImpactReport;
      })
      .filter(r => r.from && r.to &&
        r.from.getTime() >= start.getTime() - DAY_MS && r.to.getTime() <= end.getTime() + DAY_MS)
      .sort((a, b) => a.from.getTime() - b.from.getTime());
  }

  async addPlaceholderPhoto(photoUrl: string, createdBy: string): Promise<string> {
    const ref = await this.afs.collection(PLACEHOLDER_PHOTO_COLLECTION).add({
      photoUrl,
      createdBy,
      createdAt: new Date(),
    });
    return ref.id;
  }

  getPlaceholderPhotos(): Observable<PlaceholderPhoto[]> {
    return this.afs.collection<PlaceholderPhoto>(PLACEHOLDER_PHOTO_COLLECTION, ref =>
      ref.orderBy('createdAt', 'desc')
    ).valueChanges({ idField: 'id' });
  }

  async deletePlaceholderPhoto(id: string): Promise<void> {
    await this.afs.collection(PLACEHOLDER_PHOTO_COLLECTION).doc(id).delete();
  }

  // Ordered list of quarter options for a given calendar year, most useful for the config step's dropdown.
  /** Start year of the financial year (April–March) containing `date`: Sep 2026 → 2026 (FY
   *  2026-27), Feb 2027 → 2026 as well. */
  financialYearStart(date = new Date()): number {
    return date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  }

  /** "2026-2027" for the FY starting in 2026 — the Year dropdown's display text. */
  financialYearLabel(fyStart: number): string {
    return `${fyStart}-${fyStart + 1}`;
  }

  /** The four quarters of the financial year starting in April `fyStart`, labelled e.g.
   *  "Q1 2026-27" (Apr–Jun 2026) … "Q4 2026-27" (Jan–Mar 2027). The label becomes the report's
   *  quarterLabel and its default cover title ("Q1 2026-27 REPORT"). */
  getQuartersForFinancialYear(fyStart: number): { label: string; fullLabel: string; start: Date; end: Date }[] {
    const shortFy = `${fyStart}-${String(fyStart + 1).slice(-2)}`;
    return FY_QUARTERS.map(q => {
      const year = fyStart + q.yearOffset;
      const start = new Date(year, q.startMonth, 1);
      const end = new Date(year, q.endMonth + 1, 0);
      return {
        label: `${q.label} ${shortFy}`,
        fullLabel: `${this.ordinal(start)} to ${this.ordinal(end)}`,
        start,
        end,
      };
    });
  }

  private ordinal(d: Date): string {
    const day = d.getDate();
    const suffix = day % 10 === 1 && day !== 11 ? 'st'
      : day % 10 === 2 && day !== 12 ? 'nd'
      : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
    return `${day}${suffix} ${d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
  }

  // ── Data fetching (real Firestore queries) ─────────────────────────────────
  // Every figure follows the report that already owns it, so the narrative report agrees with them:
  //   - admissions, caregivers and the age / gender / hospital charts: the admission-cycle
  //     records (env.admissionCycles, one doc per stay) admitted in the period — the same source
  //     as the annual impact report;
  //   - bed occupancy: nights stayed over the centre's units (env.centres) times the period's days;
  //   - education counts and scores: the session records, counted as the Education Impact Report
  //     counts them (impact-reports.component.ts);
  //   - counselling counts and coping charts: the session and assessment records, as in the
  //     Counselling Impact Report (impact-report-counseling.component.ts).
  // Raw records are loaded once per centre + period and shared by the methods below.

  private readonly loads = new Map<string, Promise<any>>();

  /** Runs `load` once per key and shares the result; a failed load is forgotten so it can retry. */
  private once<T>(kind: string, centreCode: string, start: Date, end: Date, load: () => Promise<T>): Promise<T> {
    const key = `${kind}|${centreCode}|${start.getTime()}|${end.getTime()}`;
    let p = this.loads.get(key) as Promise<T> | undefined;
    if (!p) {
      p = load();
      this.loads.set(key, p);
      p.catch(() => this.loads.delete(key));
    }
    return p;
  }

  /** Forgets the loaded records, so the next fetch reads Firestore again. */
  clearFetchedData(): void {
    this.loads.clear();
  }

  private startOfDay(d: Date): Date { return new Date(new Date(d).setHours(0, 0, 0, 0)); }
  private endOfDay(d: Date): Date { return new Date(new Date(d).setHours(23, 59, 59, 999)); }

  private toDate(v: any): Date | null {
    if (!v) return null;
    const d: Date = v instanceof Date ? v : typeof v.toDate === 'function' ? v.toDate() : new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }

  private async docs(path: string, query: (ref: any) => any): Promise<any[]> {
    const snap = await this.afs.collection(path, query).get().toPromise();
    return (snap?.docs || []).map(d => d.data());
  }

  /** Stays admitted to the centre in the period (first admissions and follow-ups). The range is on
   *  one field and the centre is filtered in memory, so no composite index is needed. */
  private admittedCycles(centreCode: string, start: Date, end: Date): Promise<any[]> {
    return this.once('cycles', centreCode, start, end, async () => {
      const all = await this.docs(this.env.admissionCycles, ref => ref
        .where('dateOfAdmission', '>=', this.startOfDay(start))
        .where('dateOfAdmission', '<=', this.endOfDay(end)));
      return all.filter(c => c?.centreCode === centreCode);
    });
  }

  /** Individual (present only) and group education sessions for the centre in the period. */
  private educationSessions(centreCode: string, centreName: string, start: Date, end: Date): Promise<{ individual: any[]; group: any[] }> {
    return this.once(`edu:${centreName}`, centreCode, start, end, async () => {
      const from = this.startOfDay(start), to = this.endOfDay(end);
      const [individual, group] = await Promise.all([
        this.docs(this.env.education, ref => ref
          .where('centreCode', '==', centreCode)
          .where('sessionDate', '>=', from).where('sessionDate', '<=', to)
          .where('present', '==', 'Present')),
        // Group sessions list their centres as {centreName, centreCode} pairs.
        this.docs(this.env.educationGroup, ref => ref
          .where('centres', 'array-contains', { centreName, centreCode })
          .where('sessionDate', '>=', from).where('sessionDate', '<=', to)),
      ]);
      return { individual, group };
    });
  }

  /** Counselling sessions (individual, group) and admission / discharge assessments. */
  private counsellingRecords(centreCode: string, start: Date, end: Date): Promise<{ individual: any[]; group: any[]; assessments: any[] }> {
    return this.once('csl', centreCode, start, end, async () => {
      const from = this.startOfDay(start), to = this.endOfDay(end);
      const [individual, group, assessments] = await Promise.all([
        this.docs(`${this.env.counseling}/allSessions/sessions`, ref => ref
          .where('centreCode', '==', centreCode)
          .where('type', 'in', ['Regular Counselling', 'Follow-up Counselling'])
          .where('date', '>=', from).where('date', '<=', to)),
        this.docs(`${this.env.counseling}/allSessions/groupSessions`, ref => ref
          .where('date', '>=', from).where('date', '<=', to)
          .where('centreCode', '==', centreCode)),
        this.docs(`${this.env.assessment}/allAssessments/assessments`, ref => ref
          .where('centreCode', '==', centreCode)
          .where('dateOfAssessment', '>=', from).where('dateOfAssessment', '<=', to)
          .where('type', 'in', ['Admission Baseline Assessment', 'Discharge Assessment'])),
      ]);
      return { individual, group, assessments };
    });
  }

  /** Bed occupancy for the period, from the stays themselves: nights children were admitted at the
   *  centre, over the centre's units (env.centres `totalUnits`) times the days in the period. A
   *  period still in progress is measured up to today. 0 when the centre has no unit count.
   *
   *  The weekly figures in env.centreRankings are not used: they stop in April 2024 and cover only
   *  nine centres, so they gave 0% for any current quarter.
   *
   *  Stays overlapping the period are those still open plus those discharged on or after its
   *  start; both queries filter on a single field (or equalities only), so no composite index. */
  private async bedOccupancyPercent(centreCode: string, start: Date, end: Date): Promise<number> {
    const DAY_MS = 86400000;
    const from = this.startOfDay(start);
    const now = new Date();
    const until = this.endOfDay(end) < now ? this.endOfDay(end) : now;
    if (until <= from) return 0;

    const [centres, open, discharged] = await Promise.all([
      this.docs(this.env.centres, ref => ref.where('centreCode', '==', centreCode).limit(1)),
      this.docs(this.env.admissionCycles, ref => ref
        .where('centreCode', '==', centreCode).where('isCurrentlyAdmitted', '==', true)),
      this.docs(this.env.admissionCycles, ref => ref.where('dateOfDischarge', '>=', from)),
    ]);
    const totalUnits = Number(centres[0]?.totalUnits) || 0;
    if (!totalUnits) return 0;

    let nights = 0;
    for (const stay of [...open, ...discharged.filter(c => c?.centreCode === centreCode)]) {
      const admitted = this.toDate(stay.dateOfAdmission);
      if (!admitted || admitted > until) continue;
      const left = this.toDate(stay.dateOfDischarge) || now;
      const overlap = Math.min(left.getTime(), until.getTime()) - Math.max(admitted.getTime(), from.getTime());
      if (overlap > 0) nights += overlap / DAY_MS;
    }
    const capacity = totalUnits * ((until.getTime() - from.getTime()) / DAY_MS);
    // Capped at 100: a stay never closed in the records would otherwise push it past full.
    return Math.min(100, Math.round((nights / capacity) * 100));
  }

  async getBeneficiaryStats(centreCode: string, start: Date, end: Date, centreName = ''): Promise<BeneficiaryStats> {
    const [cycles, bedOccupancyPercent, edu, csl] = await Promise.all([
      this.admittedCycles(centreCode, start, end),
      this.bedOccupancyPercent(centreCode, start, end),
      this.getEducationSessionCounts(centreCode, start, end, centreName),
      this.getCounsellingSessionCounts(centreCode, start, end),
    ]);
    const named = (v: any) => (typeof v === 'string' && v.trim() ? 1 : 0);
    return {
      bedOccupancyPercent,
      admissionCount: cycles.length,
      // The guardians named on each stay are the caregivers accommodated with the child.
      caregiverAccommodationCount: cycles.reduce((n, c) => n + named(c.guardian1Name) + named(c.guardian2Name), 0),
      educationSessionCount: edu.teacherSessions + edu.volunteerSessions + edu.specialDaysObserved,
      counsellingSessionCount: csl.individualSessions + csl.psychologicalInterventions + csl.groupSessions,
    };
  }

  /** Children admitted in the period by age at admission. The five standard bands always show;
   *  older children get a band of their own only when there are any. */
  async getAgeWiseData(centreCode: string, start: Date, end: Date): Promise<CountDatum[]> {
    const cycles = await this.admittedCycles(centreCode, start, end);
    const bands = [
      { label: '0-3', max: 3 }, { label: '4-7', max: 7 }, { label: '8-10', max: 10 },
      { label: '11-14', max: 14 }, { label: '15-18', max: 18 },
    ];
    const counts = bands.map(() => 0);
    let older = 0;
    for (const c of cycles) {
      let age: number | null = typeof c.age === 'number' ? c.age : null;
      if (age === null) {
        const dob = this.toDate(c.dob), admitted = this.toDate(c.dateOfAdmission);
        if (dob && admitted) age = Math.max(0, Math.floor((admitted.getTime() - dob.getTime()) / 31557600000));
      }
      if (age === null) continue;
      const i = bands.findIndex(b => age! <= b.max);
      if (i >= 0) counts[i]++; else older++;
    }
    const out = bands.map((b, i) => ({ label: b.label, count: counts[i] }));
    if (older) out.push({ label: '19+', count: older });
    return out;
  }

  async getGenderWiseData(centreCode: string, start: Date, end: Date): Promise<CountDatum[]> {
    const cycles = await this.admittedCycles(centreCode, start, end);
    const counts = new Map<string, number>([['Male', 0], ['Female', 0]]);
    for (const c of cycles) {
      const g = String(c.gender || '').trim().toLowerCase();
      const label = g.startsWith('m') ? 'Male' : g.startsWith('f') ? 'Female' : g ? 'Other' : '';
      if (label) counts.set(label, (counts.get(label) || 0) + 1);
    }
    return Array.from(counts, ([label, count]) => ({ label, count }));
  }

  /** Children admitted in the period by treating hospital, busiest first. Long hospital names are
   *  shortened to their initials so they fit under the chart's bars (editable in the wizard). */
  async getHospitalWiseData(centreCode: string, start: Date, end: Date): Promise<CountDatum[]> {
    const cycles = await this.admittedCycles(centreCode, start, end);
    const counts = new Map<string, number>();
    for (const c of cycles) {
      const name = String(c.hospital || '').trim();
      if (name) counts.set(name, (counts.get(name) || 0) + 1);
    }
    const short = (name: string) => name.length <= 16 ? name
      : name.split(/[\s&-]+/).filter(w => w && !/^(and|of|the|for)$/i.test(w)).map(w => w[0].toUpperCase()).join('');
    return Array.from(counts, ([label, count]) => ({ label: short(label), count }))
      .sort((a, b) => b.count - a.count);
  }

  /** Teacher sessions, volunteer sessions and special days, as the Education Impact Report counts
   *  them: a session with a volunteer named is a volunteer session, otherwise a teacher session;
   *  a group session counted "by children" adds one volunteer session per child; group sessions
   *  of type "Special Day" are the special days and are kept out of the teacher count, so the
   *  three add up to that report's total. */
  async getEducationSessionCounts(centreCode: string, start: Date, end: Date, centreName = ''): Promise<{ teacherSessions: number; volunteerSessions: number; specialDaysObserved: number }> {
    const { individual, group } = await this.educationSessions(centreCode, centreName, start, end);
    const hasVolunteer = (s: any) => !!String(s?.volunteerName || '').trim();
    const isSpecialDay = (s: any) => s?.sessionType === 'Special Day';
    const childrenCounted = group
      .filter(s => s?.countType === 'bychildren' && Array.isArray(s.children))
      .reduce((n, s) => n + s.children.length, 0);
    return {
      teacherSessions: individual.filter(s => !hasVolunteer(s)).length
        + group.filter(s => !hasVolunteer(s) && !isSpecialDay(s)).length,
      volunteerSessions: individual.filter(hasVolunteer).length + childrenCounted,
      specialDaysObserved: group.filter(isSpecialDay).length,
    };
  }

  /** Average Language / Maths / EVS-GK scores (oral + written) at discharge, for children with a
   *  Discharge Assessment in the period, against the same children's Baseline Assessment —
   *  averaged exactly as the Education Impact Report does, so the two charts agree. */
  async getEducationProgressChart(centreCode: string, start: Date, end: Date, centreName = ''): Promise<ProgressSubjectScore[]> {
    const { individual } = await this.educationSessions(centreCode, centreName, start, end);
    const discharges = individual.filter(s => s?.sessionType === 'Discharge Assessment');
    const subjects: { subject: string; oral: string; written: string }[] = [
      { subject: 'Language', oral: 'languageOral', written: 'languageWritten' },
      { subject: 'Maths', oral: 'mathsOral', written: 'mathsWritten' },
      { subject: 'EVS/GK', oral: 'evsgkOral', written: 'evsgkWritten' },
    ];
    const round2 = (n: number) => Number(n.toFixed(2));
    // Discharge scores count only when both the oral and written marks are filled in
    // (Education Impact Report, getTotalDischarges).
    const dischargeAverage = (oral: string, written: string) =>
      round2(discharges.reduce((t, r) => (r?.[oral] && r?.[written]) ? t + Number(r[oral]) + Number(r[written]) : t, 0) / discharges.length);
    // Baseline scores count when either mark is filled in, and the total is divided by the number
    // of discharge assessments, not by the children found (getTotalBaselinesForChildren).
    const baselineAverage = (rows: any[], oral: string, written: string) =>
      round2(rows.reduce((t, r) => (r?.[oral] || r?.[written]) ? t + Number(r[oral] || 0) + Number(r[written] || 0) : t, 0) / discharges.length);
    if (!discharges.length) return subjects.map(s => ({ subject: s.subject, baseline: 0, discharge: 0 }));


    const childCodes = Array.from(new Set(discharges.map(d => d.childCode).filter(Boolean))) as string[];
    const chunks: string[][] = [];
    for (let i = 0; i < childCodes.length; i += 10) chunks.push(childCodes.slice(i, i + 10)); // 'in' takes 10
    const baselines = (await Promise.all(chunks.map(chunk =>
      this.docs(this.env.education, ref => ref
        .where('sessionType', '==', 'Baseline Assessment').where('childCode', 'in', chunk))))).flat();

    return subjects.map(s => ({
      subject: s.subject,
      baseline: baselineAverage(baselines, s.oral, s.written),
      discharge: dischargeAverage(s.oral, s.written),
    }));
  }

  /** Individual and group counselling sessions, and admission / discharge assessments (the
   *  report's "Psychological interventions"), as the Counselling Impact Report counts them. */
  async getCounsellingSessionCounts(centreCode: string, start: Date, end: Date): Promise<{ individualSessions: number; psychologicalInterventions: number; groupSessions: number }> {
    const { individual, group, assessments } = await this.counsellingRecords(centreCode, start, end);
    return {
      individualSessions: individual.length,
      psychologicalInterventions: assessments.length,
      groupSessions: group.length,
    };
  }

  /** Coping at admission and at discharge, as shares of all assessment answers: "No" (no
   *  difficulty) is able to cope, "Sometimes" is sometimes able to, "Yes" is difficulty in
   *  coping — the Counselling Impact Report's reading of the same answers. */
  async getCopingCharts(centreCode: string, start: Date, end: Date): Promise<{ admission: CopingSegment[]; discharge: CopingSegment[] }> {
    const { assessments } = await this.counsellingRecords(centreCode, start, end);
    const chart = (type: string): CopingSegment[] => {
      const answers: string[] = assessments.filter(a => a?.type === type)
        .flatMap(a => (Array.isArray(a.assessment) ? a.assessment : []).map((q: any) => q?.ans));
      const count = (ans: string) => answers.filter(a => a === ans).length;
      const yes = count('Yes'), no = count('No'), sometimes = count('Sometimes');
      const total = yes + no + sometimes;
      const pct = (n: number) => total ? Math.round((n / total) * 1000) / 10 : 0;
      return [
        { label: 'Able to Cope up', value: pct(no) },
        { label: 'Sometimes able to', value: pct(sometimes) },
        { label: 'Difficulty in Coping', value: pct(yes) },
      ];
    };
    return { admission: chart('Admission Baseline Assessment'), discharge: chart('Discharge Assessment') };
  }

}
